import { observer } from 'mobx-react-lite';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Badge } from '@/components/ui/Badge';
import { Button, Tap } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';
import { ReferralCodeSection, ReferralModal } from '@/features/referral';
import { REFERRAL_CREDIT } from '@/features/referral/referral.constants';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import { PLAN_FEATURES } from './subscription.constants';
import { usePricingPageCopy } from './subscription.copy';
import { useSubscription } from './subscription.query';
import { subscriptionStore } from './subscription.store';
import {
  type PricingPlan,
  SubscriptionPlan,
  formatPriceValue,
  getDiscountedPrice,
} from './subscription.types';
import { usePlusOfferLabel } from './use-plus-offer-label';

function FeaturesList({ features }: { features: string[] }) {
  return (
    <ul className="space-y-2">
      {features.map((feature, index) => (
        <li
          key={index}
          className="flex items-start gap-2 text-sm text-foreground"
        >
          <Icons.check className="w-3.5 h-3.5 text-success-11 flex-shrink-0 mt-0.5" />
          {feature}
        </li>
      ))}
    </ul>
  );
}

type SegmentOption = {
  value: string;
  label: ReactNode;
  className?: string;
};

export type PlanOptionCardData = {
  title: string;
  tagline?: string;
  price: number;
  originalPrice?: number;
  priceSubtext?: ReactNode;
  badge?: string;
  features: string[];
  featuresHeader?: string;
  buttonText: ReactNode;
  buttonVariant: 'default' | 'secondary' | 'outline';
  className?: string;
  onClick?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  segmentedControl?: {
    value: string;
    onValueChange: (value: string) => void;
    options: SegmentOption[];
    ariaLabel: string;
    placement?: 'header' | 'cta';
  };
};

export function PlanOptionCard({ data }: { data: PlanOptionCardData }) {
  const {
    title,
    price,
    originalPrice,
    priceSubtext,
    badge,
    features,
    featuresHeader,
    buttonText,
    buttonVariant,
    className,
    onClick,
    isLoading,
    disabled,
    segmentedControl,
  } = data;

  return (
    <YStack
      className={cn(
        'bg-surface border border-border dark:border-border-strong rounded-xl px-6 pb-6 pt-4 flex flex-col',
        className
      )}
    >
      <XStack className="items-center gap-2 mb-5 min-h-[36px]">
        <h4 className="text-xl font-medium font-serif tracking-tight">
          {title}
        </h4>
        {badge && (
          <Badge variant="success-subtle" size="xs" className="font-semibold">
            {badge}
          </Badge>
        )}
        {segmentedControl && segmentedControl.placement !== 'cta' && (
          <SegmentedControl.Root
            value={segmentedControl.value}
            onValueChange={segmentedControl.onValueChange}
            className="ml-auto shrink-0"
          >
            <SegmentedControl.List
              aria-label={segmentedControl.ariaLabel}
              className="h-auto w-auto flex-col items-start gap-1.5 border-0 before:hidden"
            >
              {segmentedControl.options.map((opt) => (
                <SegmentedControl.Trigger
                  key={opt.value}
                  value={opt.value}
                  disabled={disabled}
                  className={cn(
                    'h-auto justify-start rounded-none px-0 py-0 text-[11px] leading-tight',
                    opt.className
                  )}
                >
                  {opt.label}
                </SegmentedControl.Trigger>
              ))}
            </SegmentedControl.List>
          </SegmentedControl.Root>
        )}
      </XStack>

      <YStack className="mb-6 gap-1">
        <XStack className="items-baseline gap-2 [font-variant-numeric:tabular-nums]">
          <span className="text-4xl font-normal text-foreground tracking-tight">
            ${formatPriceValue(price)}
          </span>
          <span className="text-sm text-foreground-muted">/mo</span>
          {originalPrice != null && (
            <span className="text-sm text-foreground-subtle line-through">
              ${formatPriceValue(originalPrice)}/mo
            </span>
          )}
        </XStack>
        {priceSubtext != null && (
          <span className="text-xs text-foreground-muted">{priceSubtext}</span>
        )}
      </YStack>

      <div className="flex-1 border-t border-border pt-5">
        {featuresHeader && (
          <p className="text-xs text-foreground-muted mb-3">{featuresHeader}</p>
        )}
        <FeaturesList features={features} />
      </div>

      <YStack className="mt-7 gap-3">
        {segmentedControl && segmentedControl.placement === 'cta' && (
          <SegmentedControl.Root
            value={segmentedControl.value}
            onValueChange={segmentedControl.onValueChange}
          >
            <SegmentedControl.List
              aria-label={segmentedControl.ariaLabel}
              className="w-full grid grid-flow-col auto-cols-fr h-9"
            >
              {segmentedControl.options.map((opt) => (
                <SegmentedControl.Trigger
                  key={opt.value}
                  value={opt.value}
                  disabled={disabled}
                  className={cn('w-full', opt.className)}
                >
                  {opt.label}
                </SegmentedControl.Trigger>
              ))}
            </SegmentedControl.List>
          </SegmentedControl.Root>
        )}
        <Tap>
          <Button
            variant={buttonVariant}
            onClick={onClick}
            isLoading={isLoading}
            disabled={disabled}
            className="w-full"
          >
            {buttonText}
          </Button>
        </Tap>
      </YStack>
    </YStack>
  );
}

const ReferralCtaRow = observer(() => {
  const rewardType = subscriptionStore.state.userReferralRewardType;
  const isActivity = rewardType === 'activity_based';
  const credit = isActivity
    ? `${REFERRAL_CREDIT.ACTIVITY} credits`
    : `${REFERRAL_CREDIT.PAYMENT} credits`;
  const subtext = isActivity
    ? 'who uses LittleBird for 3 days'
    : 'who subscribes';
  const hasTrackedViewRef = useRef(false);

  useEffect(() => {
    if (hasTrackedViewRef.current) return;
    if (rewardType == null) return;
    hasTrackedViewRef.current = true;
    trackEvent('REFERRAL_CTA_VIEWED', {
      experiment_variant: rewardType,
    });
  }, [rewardType]);

  return (
    <XStack className="items-center gap-3 bg-emerald-500/5 dark:bg-emerald-500/10 px-5 py-3">
      <Icons.refer className="size-5 text-success-11 dark:text-success-9 shrink-0" />
      <p className="flex-1 text-sm text-emerald-900 dark:text-emerald-50">
        Earn <span className="font-semibold">{credit}</span> for each friend{' '}
        {subtext}
      </p>
      <Button
        variant="outline"
        size="sm"
        className="border-emerald-500/30 text-success-11 dark:text-success-9 hover:bg-emerald-500/10 shrink-0"
        onClick={() => {
          trackEvent('REFERRAL_CTA_CLICKED', {
            experiment_variant: isActivity ? 'activity_based' : 'payment_based',
          });
          ModalsManager.show(ReferralModal, {});
        }}
      >
        Start Referring
      </Button>
    </XStack>
  );
});

const useHorizontalScrollArrows = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < maxScroll - 2);
  }, []);

  const setScroller = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current?.removeEventListener('scroll', update);
      ref.current = node;
      node?.addEventListener('scroll', update, { passive: true });
      update();
    },
    [update]
  );

  useEffect(() => {
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);

  const scrollByCard = (direction: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    const step = card ? card.offsetWidth + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  return { setScroller, canScrollLeft, canScrollRight, scrollByCard };
};

const BillingToggle = ({
  value,
  onChange,
  savingsPercent,
  disabled,
}: {
  value: 'monthly' | 'annual';
  onChange: (value: 'monthly' | 'annual') => void;
  savingsPercent: number;
  disabled?: boolean;
}) => {
  const optionClass = (isActive: boolean) =>
    cn(
      'inline-flex items-center gap-2 rounded-full px-5 py-1.5 text-sm font-medium transition-colors disabled:opacity-50',
      isActive
        ? 'bg-background text-foreground shadow-sm'
        : 'text-foreground-muted hover:text-foreground'
    );

  return (
    <XStack className="rounded-full border border-border-strong bg-surface-active p-1">
      <button
        type="button"
        aria-pressed={value === 'monthly'}
        disabled={disabled}
        onClick={() => onChange('monthly')}
        className={optionClass(value === 'monthly')}
      >
        Monthly
      </button>
      <button
        type="button"
        aria-pressed={value === 'annual'}
        disabled={disabled}
        onClick={() => onChange('annual')}
        className={optionClass(value === 'annual')}
      >
        Annual
        {savingsPercent > 0 && (
          <Badge
            variant="success-subtle"
            size="xs"
            className="rounded-full font-semibold"
          >
            Save {savingsPercent}%
          </Badge>
        )}
      </button>
    </XStack>
  );
};

export type PlanOption = PlanOptionCardData & { id: string };

export type PlanSelectorView = {
  planOptions: PlanOption[];
  billingInterval: 'monthly' | 'annual';
  setBillingInterval: (billingInterval: 'monthly' | 'annual') => void;
  annualSavingsPercent: number;
};

type PlanSelectorProps = {
  leadingFeature?: string;
  renderPlans?: (view: PlanSelectorView) => ReactNode;
};

export const PlanSelector = observer((props: PlanSelectorProps) => {
  const leadingFeatures = props.leadingFeature ? [props.leadingFeature] : [];
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>(
    'annual'
  );
  const [proTier, setProTier] = useState<'5x' | '12x'>('5x');
  const subscription = useSubscription();
  const user = userStore.user;
  const copy = usePricingPageCopy();
  const plusOfferLabel = usePlusOfferLabel();
  const { setScroller, canScrollLeft, canScrollRight, scrollByCard } =
    useHorizontalScrollArrows();
  const canScroll = canScrollLeft || canScrollRight;

  const isFreemiumUser = user?.feature_flags?.is_freemium_user;
  const isStudentDiscountActive = user?.feature_flags?.student_discount;

  const showPromoBanner = Boolean(
    copy.promoBanner?.title || copy.promoBanner?.body
  );
  const proDiscountPercent = copy.pro?.discountPercent ?? 0;
  const showProDiscount = proDiscountPercent > 0;

  useEffect(() => {
    if (
      subscription.pricingPlans.length === 0 &&
      !subscription.pricingPlansLoading
    ) {
      subscription.fetchPricingPlans();
    }
  }, [subscription]);

  const plusMonthlyPlan = subscription.pricingPlans.find(
    (p) => p.id === SubscriptionPlan.PLUS_MONTHLY
  );
  const plusAnnualPlan = subscription.pricingPlans.find(
    (p) => p.id === SubscriptionPlan.PLUS_ANNUAL
  );

  const showSixtyDayTrialText = isStudentDiscountActive && isFreemiumUser;
  const proPlan = subscription.pricingPlans.find(
    (p) => p.id === SubscriptionPlan.PRO_MONTHLY
  );
  const pro2Plan = subscription.pricingPlans.find(
    (p) => p.id === SubscriptionPlan.PRO2_MONTHLY
  );
  const proPrice = proPlan?.price ? proPlan.price / 100 : 100;
  const pro2Price = pro2Plan?.price ? pro2Plan.price / 100 : 200;
  const proDiscountedPrice = showProDiscount
    ? getDiscountedPrice(proPrice, proDiscountPercent)
    : proPrice;
  const pro2DiscountedPrice = showProDiscount
    ? getDiscountedPrice(pro2Price, proDiscountPercent)
    : pro2Price;

  const plusMonthlyPrice = plusMonthlyPlan?.price
    ? plusMonthlyPlan.price / 100
    : 0;
  const plusMonthlyOriginalPrice = plusMonthlyPlan?.original_price
    ? plusMonthlyPlan.original_price / 100
    : null;
  const plusAnnualTotal = plusAnnualPlan?.price
    ? plusAnnualPlan.price / 100
    : 0;
  const plusAnnualPerMonth = plusAnnualTotal
    ? Math.round((plusAnnualTotal / 12) * 100) / 100
    : 0;
  const annualSavingsPercent =
    plusMonthlyPrice > 0 && plusAnnualPerMonth > 0
      ? Math.round((1 - plusAnnualPerMonth / plusMonthlyPrice) * 100)
      : 0;

  const activePlusPlan =
    billingInterval === 'annual' && plusAnnualPlan
      ? plusAnnualPlan
      : plusMonthlyPlan;
  const activePlusPrice =
    billingInterval === 'annual' ? plusAnnualPerMonth : plusMonthlyPrice;
  const showMonthlyPromo =
    billingInterval === 'monthly' && plusMonthlyOriginalPrice != null;

  const plusPayloadPrice =
    billingInterval === 'annual'
      ? copy.plus?.annualPrice
      : copy.plus?.monthlyPrice;
  const usePlusPayloadPrice =
    plusPayloadPrice !== undefined && plusPayloadPrice < activePlusPrice;
  const plusDisplayedPrice = usePlusPayloadPrice
    ? plusPayloadPrice
    : activePlusPrice;
  const billedSubtext = (annualTotal: number) =>
    billingInterval === 'annual' && annualTotal > 0
      ? `Billed $${formatPriceValue(annualTotal)}/year`
      : 'Billed monthly';
  const annualStrikethrough = (monthlyPrice: number, displayedPrice: number) =>
    billingInterval === 'annual' && monthlyPrice > displayedPrice
      ? monthlyPrice
      : undefined;
  const plusOriginalPrice = usePlusPayloadPrice
    ? activePlusPrice
    : showMonthlyPromo
      ? (plusMonthlyOriginalPrice ?? undefined)
      : annualStrikethrough(plusMonthlyPrice, plusDisplayedPrice);
  const plusPriceSubtext = billedSubtext(plusAnnualTotal);
  const inferredPlusSavingsPercent =
    usePlusPayloadPrice && activePlusPrice > 0
      ? Math.round((1 - plusDisplayedPrice / activePlusPrice) * 100)
      : 0;
  const plusBadge = usePlusPayloadPrice
    ? (copy.plus?.badge ??
      (inferredPlusSavingsPercent > 0
        ? `${inferredPlusSavingsPercent}% off`
        : undefined))
    : showMonthlyPromo
      ? (copy.plus?.badge ?? 'Student')
      : undefined;

  const powerMonthlyPlan = subscription.pricingPlans.find(
    (p) => p.id === SubscriptionPlan.POWER_MONTHLY
  );
  const powerAnnualPlan = subscription.pricingPlans.find(
    (p) => p.id === SubscriptionPlan.POWER_ANNUAL
  );
  const powerMonthlyPrice = powerMonthlyPlan?.price
    ? powerMonthlyPlan.price / 100
    : 0;
  const powerAnnualTotal = powerAnnualPlan?.price
    ? powerAnnualPlan.price / 100
    : 0;
  const powerAnnualPerMonth = powerAnnualTotal
    ? Math.floor(powerAnnualTotal / 12)
    : 0;
  const activePowerPlan =
    billingInterval === 'annual' && powerAnnualPlan
      ? powerAnnualPlan
      : powerMonthlyPlan;
  const activePowerPrice =
    billingInterval === 'annual' ? powerAnnualPerMonth : powerMonthlyPrice;
  const powerOriginalPrice = annualStrikethrough(
    powerMonthlyPrice,
    activePowerPrice
  );
  const powerPriceSubtext = billedSubtext(powerAnnualTotal);
  const powerTitle = (powerMonthlyPlan ?? powerAnnualPlan)?.name ?? 'Power';

  if (subscription.pricingPlansLoading) {
    return (
      <YStack className="gap-4">
        <Skeleton className="h-16 w-full rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </YStack>
    );
  }

  const handleUpgrade = async (plan: PricingPlan | undefined) => {
    if (!plan) return;

    setLoadingPlanId(plan.id);
    try {
      trackEvent('SUBSCRIPTION_PAGE_TRIAL_STARTED', {
        plan_id: plan.id,
      });
      await subscription.createCheckoutSession({
        price_id: plan.stripe_price_id,
      });
    } catch (error) {
      console.error('Failed to create checkout session:', error);
    } finally {
      setLoadingPlanId(null);
    }
  };

  const isAnyLoading = loadingPlanId !== null;

  const plusCtaText =
    copy.plus?.ctaText ??
    (copy.plus?.trialDays !== undefined
      ? `Start ${copy.plus.trialDays}-day free trial`
      : showSixtyDayTrialText
        ? 'Start 60-day free trial'
        : plusOfferLabel
          ? plusOfferLabel.ctaLabel
          : isFreemiumUser
            ? 'Start 14-day free trial'
            : 'Choose Plus');

  const proOption = (tier: '5x' | '12x'): PlanOption => {
    const tierPlan = tier === '5x' ? proPlan : pro2Plan;
    const tierLabel = tier === '5x' ? '5×' : '12×';
    return {
      id: `pro-${tier}`,
      title: `Pro ${tierLabel}`,
      tagline: `${tierLabel} more usage than Plus`,
      badge: showProDiscount
        ? (copy.pro?.badge ?? `${proDiscountPercent}% off`)
        : undefined,
      price: tier === '5x' ? proDiscountedPrice : pro2DiscountedPrice,
      originalPrice: showProDiscount
        ? tier === '5x'
          ? proPrice
          : pro2Price
        : undefined,
      priceSubtext: 'Billed monthly',
      features: [
        ...leadingFeatures,
        ...PLAN_FEATURES[
          tier === '5x'
            ? SubscriptionPlan.PRO_MONTHLY
            : SubscriptionPlan.PRO2_MONTHLY
        ],
      ],
      featuresHeader: 'Includes everything in Plus, and:',
      buttonText: `Upgrade to Pro ${tierLabel}`,
      buttonVariant: 'secondary',
      onClick: () => handleUpgrade(tierPlan),
      isLoading: tierPlan !== undefined && loadingPlanId === tierPlan.id,
      disabled: isAnyLoading,
    };
  };

  const proTierControl: PlanOptionCardData['segmentedControl'] = {
    ariaLabel: 'Pro usage tier',
    value: proTier,
    onValueChange: (value) => {
      setProTier(value as '5x' | '12x');
    },
    placement: 'cta',
    options: [
      { value: '5x', label: '5×' },
      { value: '12x', label: '12×' },
    ],
  };

  const planOptions: PlanOption[] = [
    {
      id: 'plus',
      title: 'Plus',
      tagline: 'Advanced intelligence and more usage',
      badge: plusBadge,
      price: plusDisplayedPrice,
      originalPrice: plusOriginalPrice,
      priceSubtext: plusPriceSubtext,
      features: [
        ...leadingFeatures,
        ...PLAN_FEATURES[SubscriptionPlan.PLUS_MONTHLY],
      ],
      featuresHeader: 'Includes everything in Basic, and:',
      buttonText: plusCtaText,
      buttonVariant: 'default',
      onClick: () => handleUpgrade(activePlusPlan),
      isLoading:
        loadingPlanId === SubscriptionPlan.PLUS_MONTHLY ||
        loadingPlanId === SubscriptionPlan.PLUS_ANNUAL,
      disabled: isAnyLoading,
    },
    {
      id: 'power',
      title: powerTitle,
      tagline: '2.5× more usage than Plus',
      badge: 'New',
      price: activePowerPrice,
      originalPrice: powerOriginalPrice,
      priceSubtext: powerPriceSubtext,
      features: [
        ...leadingFeatures,
        ...PLAN_FEATURES[SubscriptionPlan.POWER_MONTHLY],
      ],
      featuresHeader: 'Includes everything in Plus, and:',
      buttonText: 'Choose Power',
      buttonVariant: 'secondary',
      onClick: () => handleUpgrade(activePowerPlan),
      isLoading:
        loadingPlanId === SubscriptionPlan.POWER_MONTHLY ||
        loadingPlanId === SubscriptionPlan.POWER_ANNUAL,
      disabled: isAnyLoading,
    },
    {
      ...proOption(proTier),
      id: 'pro',
      title: 'Pro',
      segmentedControl: proTierControl,
    },
  ];

  return (
    <YStack className="gap-4">
      {plusOfferLabel && (
        <XStack className="items-center gap-3 bg-blue-500/5 dark:bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3">
          <Icons.clock className="size-4 text-blue-600 dark:text-blue-400 shrink-0" />
          <p className="text-sm text-blue-900 dark:text-blue-50">
            <span className="font-semibold">
              Limited offer: {plusOfferLabel.trialDays} days of Plus, free.
            </span>{' '}
            Offer ends {plusOfferLabel.expiryLabel}.
          </p>
        </XStack>
      )}

      {showPromoBanner && (
        <XStack className="items-center gap-3 bg-blue-500/5 dark:bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3">
          <Icons.clock className="size-4 text-blue-600 dark:text-blue-400 shrink-0" />
          <p className="text-sm text-blue-900 dark:text-blue-50">
            {copy.promoBanner?.title && (
              <>
                <span className="font-semibold">{copy.promoBanner.title}</span>
                {copy.promoBanner.body ? ' — ' : ''}
              </>
            )}
            {copy.promoBanner?.body}
          </p>
        </XStack>
      )}

      {isStudentDiscountActive && (
        <XStack className="items-center gap-3 bg-emerald-500/5 dark:bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3">
          <Icons.check className="size-4 text-success-11 dark:text-success-9 shrink-0" />
          <p className="text-sm text-emerald-900 dark:text-emerald-50">
            <span className="font-semibold">Student discount active</span> —
            choose Plus monthly to see your discounted price.
          </p>
        </XStack>
      )}

      {props.renderPlans?.({
        planOptions: [
          ...planOptions.slice(0, -1),
          proOption('5x'),
          proOption('12x'),
        ],
        billingInterval,
        setBillingInterval,
        annualSavingsPercent,
      })}
      {!props.renderPlans && (
        <>
          <XStack className="items-center justify-between gap-4">
            <BillingToggle
              value={billingInterval}
              onChange={setBillingInterval}
              savingsPercent={annualSavingsPercent}
              disabled={isAnyLoading}
            />

            {canScroll && (
              <XStack className="gap-2">
                <Button
                  variant="secondary"
                  size="icon"
                  className="rounded-full"
                  aria-label="Previous plans"
                  disabled={!canScrollLeft}
                  onClick={() => scrollByCard(-1)}
                >
                  <Icons.chevronLeft />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="rounded-full"
                  aria-label="Next plans"
                  disabled={!canScrollRight}
                  onClick={() => scrollByCard(1)}
                >
                  <Icons.chevronRight />
                </Button>
              </XStack>
            )}
          </XStack>

          <ScrollMask
            ref={setScroller}
            direction="horizontal"
            showEndFade
            className="flex gap-4 snap-x snap-mandatory scrollbar-hide overflow-x-auto overflow-y-hidden"
          >
            {planOptions.map((planOption) => (
              <PlanOptionCard
                key={planOption.id}
                data={{
                  ...planOption,
                  className: 'min-w-[18rem] flex-1 snap-start',
                }}
              />
            ))}
          </ScrollMask>

          <ReferralCodeSection footer={<ReferralCtaRow />} />

          <p className="text-sm text-foreground-subtle text-center py-2">
            Want Littlebird for your team or business?{' '}
            <a
              href="https://littlebird.ai/enterprise"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline underline-offset-4 hover:text-foreground transition-colors"
            >
              Contact us
              <Icons.externalLink2 className="size-3.5 shrink-0" />
            </a>
          </p>
        </>
      )}
    </YStack>
  );
});
